/**
 * Breadcrumbs — auto-derived from URL, with per-page override.
 *
 * Pages call `useBreadcrumbs([...])` on mount to replace the auto-derived
 * crumbs (typically for dynamic names: patient, deal title, doctor). If no
 * override is set, `useCurrentBreadcrumbs()` falls back to URL-based labels.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

export interface Crumb {
  label: string
  to?: string
}

interface BreadcrumbCtx {
  override: Crumb[] | null
  setOverride: (c: Crumb[] | null) => void
}

const BreadcrumbContext = createContext<BreadcrumbCtx | null>(null)

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [override, setOverride] = useState<Crumb[] | null>(null)
  const value = useMemo(() => ({ override, setOverride }), [override])
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
}

/** Pages register dynamic crumbs via this hook. Cleared on unmount. */
export function useBreadcrumbs(crumbs: Crumb[]): void {
  const ctx = useContext(BreadcrumbContext)
  const key = crumbs.map((c) => `${c.label}|${c.to ?? ''}`).join(';')
  useEffect(() => {
    if (!ctx) return
    ctx.setOverride(crumbs)
    return () => ctx.setOverride(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}

/** Only the crumbs a page registered (the open record's name), without the URL-derived ones. */
export function useBreadcrumbOverride(): Crumb[] | null {
  return useContext(BreadcrumbContext)?.override ?? null
}

/** Route segment → label. Unknown segments pass through (title-cased). */
const ROUTE_LABELS: Record<string, string> = {
  hoje: 'Hoje',
  'minha-conta': 'Minha conta',
  agenda: 'Agenda',
  cirurgias: 'Cirurgias',
  pagamentos: 'Pagamentos',
  assinaturas: 'Assinaturas',
  financeiro: 'Financeiro',
  'financeiro-v2': 'Financeiro',
  'financeiro-nucleo': 'Financeiro (núcleo novo)',
  eventos: 'Avisos do Asaas',
  'repasses-v2': 'Repasses',
  propostas: 'Deals',
  medicos: 'Médicos',
  fornecedores: 'Fornecedores',
  profissionais: 'Profissionais',
  'visao-geral': 'Visão geral',
  repasses: 'Repasses',
  servicos: 'Preços',
  configuracoes: 'Configurações',
  cadastro: 'Cadastro',
  recebimento: 'Recebimento',
  portal: 'Portal e acessos',
  comercial: 'Comercial',
  'comissoes-comercial': 'Comissões comerciais',
  'minhas-comissoes': 'Minhas comissões',
  pendencias: 'Pendências',
  integracoes: 'Integrações',
  config: 'Configurações',
  procedimentos: 'Procedimentos',
  'servicos-proprios': 'Serviços da clínica',
  requisitos: 'Requisitos',
  'tabelas-precos': 'Tabelas de venda (legado)',
  'regras-repasse': 'Regras de repasse',
  'tabelas-custo': 'Tabelas de custo',
  pacotes: 'Pacotes',
  'contas-bancarias': 'Contas Bancárias',
  'metodos-pagamento': 'Métodos de Pagamento',
  logs: 'Logs',
  arquivadas: 'Arquivadas',
  pacientes: 'Pacientes',
  feegow: 'Feegow',
}

function prettify(segment: string): string {
  // Numeric IDs show as-is; other unknown segments get title-cased.
  if (/^\d+$/.test(segment)) return segment
  return segment.charAt(0).toUpperCase() + segment.slice(1)
}

function useAutoBreadcrumbs(): Crumb[] {
  const location = useLocation()
  return useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const crumbs: Crumb[] = []
    let path = ''
    segments.forEach((seg, i) => {
      path += '/' + seg
      const isLast = i === segments.length - 1
      crumbs.push({
        label: ROUTE_LABELS[seg] ?? prettify(seg),
        to: isLast ? undefined : path,
      })
    })
    return crumbs
  }, [location.pathname])
}

export function useCurrentBreadcrumbs(): Crumb[] {
  const ctx = useContext(BreadcrumbContext)
  const auto = useAutoBreadcrumbs()
  return ctx?.override ?? auto
}
